import { db } from '@app/icare/client/db'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { FormStatusBadge } from '@app/icare/components/status/FormStatusBadge'
import { queryKit } from '@app/icare/db/query/queryKit'
import { PatientFormTypeEnum } from '@app/icare/db/schema/form/PatientFormType'
import { type ProgressReportForm, ProgressReportStatusEnum } from '@app/icare/db/schema/form/ProgressReportForm'
import type { PatientIdType } from '@app/icare/entities/patient/schema/PatientId'
import { UserInfo } from '@app/icare/entities/user/components/UserInfo'
import type { UserIdType } from '@app/icare/entities/user/schema/User'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { formatDate, formatDateTime } from '@pkg/shared/utils'
import { AddButton, ButtonLink } from '@pkg/ui/button'
import { useExtra } from '@pkg/ui/layout/PageContainer'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMemo } from 'react'
import { FaPencil } from 'react-icons/fa6'
import { sortByDateTime } from './-utils/sorters'

export const Route = createFileRoute('/_app/patienten/$id/_edit/_documents/verlaufsdokumentation')({
  component: RouteComponent,
})

function RouteComponent() {
  const { id: patientId }: { id: PatientIdType } = Route.useParams()

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db
        .selectFrom('patientForm')
        .selectAll()
        .$call(queryKit.deleted('exclude'))
        .where('patientId', '=', patientId)
        .where('type', '=', PatientFormTypeEnum.ProgressReport),
      params: queryKit.parseQuery({}),
      refetchOnMount: true,
    }),
  )

  const columns: ColumnsType<ProgressReportForm> = useMemo(
    () => [
      {
        key: 'actions',
        width: 60,
        fixed: 'left',
        render: (_, record) => (
          <ButtonLink
            to="/patienten/$id/verlaufsdokumentation/$form"
            params={{ id: record.patientId, form: record.id }}
            type="text"
            icon={<FaPencil />}
            size="small"
          />
        ),
      },
      {
        key: 'contactDate',
        title: <Trans>Kontaktdatum</Trans>,
        width: 150,
        defaultSortOrder: 'descend',
        sorter: (a, b) => sortByDateTime(a.form?.contact?.date, b.form?.contact?.date),
        render: (_, record) => formatDate(record.form?.contact?.date),
      },
      {
        key: 'status',
        title: <Trans>Status</Trans>,
        width: 120,
        sorter: (a, b) => (a.status ?? '').localeCompare(b.status ?? ''),
        render: (_, record) => <FormStatusBadge value={record.status} statusEnum={ProgressReportStatusEnum} />,
      },
      {
        key: 'createdBy',
        title: <Trans>Erstellt von</Trans>,
        dataIndex: 'createdBy',
        width: 150,
        sorter: (a, b) => (a.createdBy ?? '').localeCompare(b.createdBy ?? ''),
        render: (userId: UserIdType | null | undefined) => <UserInfo userId={userId} />,
      },
      {
        key: 'createdAt',
        title: <Trans>Erstellt am</Trans>,
        dataIndex: 'createdAt',
        width: 180,
        sorter: (a, b) => sortByDateTime(a.createdAt, b.createdAt),
        render: (value: string | null | undefined) => formatDateTime(value),
      },
      {
        key: 'updatedBy',
        title: <Trans>Geändert von</Trans>,
        dataIndex: 'updatedBy',
        width: 150,
        sorter: (a, b) => (a.updatedBy ?? '').localeCompare(b.updatedBy ?? ''),
        render: (userId: UserIdType | null | undefined) => <UserInfo userId={userId} />,
      },
    ],
    [],
  )

  useExtra(
    <AddButton size="small" to="/patienten/$id/verlaufsdokumentation/$form" params={{ id: patientId, form: 'new' }}>
      <Trans>Verlaufsdokumentation erstellen</Trans>
    </AddButton>,
    [patientId],
  )

  return (
    <Table<ProgressReportForm>
      columns={columns}
      dataSource={(data?.data as ProgressReportForm[]) ?? []}
      loading={isFetching}
      rowKey="id"
      pagination={{
        defaultPageSize: 5,
        showSizeChanger: true,
        showTotal: total => t`Gesamt ${total}`,
      }}
      scroll={{ x: 'max-content' }}
      size="small"
    />
  )
}
